import React, { useState, useEffect, memo, useCallback } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLocation } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { workOrdersState, tasksState } from '../jotai/atoms';
import './WorkOrderPage.css';
import CreateWorkOrderModal from './CreateWorkOrderModal';
import WorkOrderDetailModal from './WorkOrderDetailModal';
import WorkOrderHistoryModal from './WorkOrderHistoryModal';
import axiosInstance from '../api/axiosInstance';
import { useAuth } from '../context/AuthContext';
import Swal from 'sweetalert2';

const MemoizedCardContent = memo(({ item, handleOpenDetailModal }) => {
  let content;

  if (item.type === '배치' && item.details.assignments) {
    const { assignments, taskNumber } = item.details;
    const taskName = assignments[0]?.taskName || '';
    const manager = assignments[0]?.assignedManagers || '없음';
    const assistant = assignments[0]?.assignedAssistants || '없음';

    content = (
      <div className="card-details" style={{ textAlign: 'left' }}>
        <div><strong>종류:</strong> {item.type}</div>
        <div><strong>번호:</strong> {taskNumber}</div>
        <div><strong>공사명:</strong> {taskName}</div>
        <div><strong>책임:</strong> {manager} / <strong>보조:</strong> {assistant}</div>
      </div>
    );
  } else if (item.type === '시공통보' && item.details) {
    const { noticeDetails = '', principalSupervisor = '', assistantSupervisor = '' } = item.details;
    content = (
      <div className="card-details" style={{ textAlign: 'left' }}>
        <div><strong>종류:</strong> {item.type}</div>
        <div><strong>공사명:</strong> {noticeDetails}</div>
        <div><strong>책임:</strong> {principalSupervisor || '미지정'} / <strong>보조:</strong> {assistantSupervisor || '없음'}</div>
      </div>
    );
  } else if (item.type === '기타' && item.details) {
    const { otherTitle = '', otherDetails = '' } = item.details;
    const truncatedDetails = otherDetails.length > 30
      ? otherDetails.substring(0, 30) + '...'
      : otherDetails;

    content = (
      <div className="card-details" style={{ textAlign: 'left' }}>
        <div><strong>종류:</strong> {item.type}</div>
        <div><strong>제목:</strong> {otherTitle}</div>
        <div><strong>내용:</strong> {truncatedDetails}</div>
      </div>
    );
  } else {
    content = <div className="card-details">{item.title}</div>;
  }

  return (
    <div className="card-content-wrapper">
      {content}
      <div className="detail-button-container">
        <button
          className="detail-button"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDetailModal(item);
          }}
        >
          상세보기
        </button>
      </div>
    </div>
  );
});

const SortableItem = ({ id, item, handleOpenDetailModal }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1000 : 'auto',
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`kanban-card ${isDragging ? 'dragging' : ''}`}
    >
      <MemoizedCardContent item={item} handleOpenDetailModal={handleOpenDetailModal} />
    </div>
  );
};

// useDroppable을 사용한 드롭 가능한 컬럼
const DroppableColumn = ({ columnId, column, handleOpenDetailModal }) => {
  const {
    setNodeRef,
    isOver,
  } = useDroppable({
    id: columnId,
    data: {
      type: 'column',
      columnId: columnId,
    },
  });

  return (
    <div
      ref={setNodeRef}
      className={`kanban-column ${columnId} ${isOver ? 'column-over' : ''}`}
    >
      <h2>{column.name}</h2>
      <div className="kanban-column-items">
        {column.items.map(item => (
          <SortableItem
            key={item.id}
            id={item.id}
            item={item}
            handleOpenDetailModal={handleOpenDetailModal}
          />
        ))}
      </div>
    </div>
  );
};

function WorkOrderPage() {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [columns, setColumns] = useAtom(workOrdersState);
  const tasks = useAtomValue(tasksState);
  const setTasks = useSetAtom(tasksState);
  const [activeId, setActiveId] = useState(null);
  const [dragStartContainer, setDragStartContainer] = useState(null);
  const [historyWorkOrders, setHistoryWorkOrders] = useState([]);
  const location = useLocation();
  const initialScheduleEntry = location.state?.scheduleEntry;
  const { user } = useAuth();

  useEffect(() => {
    if (initialScheduleEntry) {
      setIsCreateModalOpen(true);
    }
  }, [initialScheduleEntry]);

  const handleOpenHistoryModal = async () => {
    try {
      const response = await axiosInstance.get('/work-orders?status=done');
      setHistoryWorkOrders(response.data);
      setIsHistoryModalOpen(true);
    } catch (error) {
      console.error('Error fetching history work orders:', error);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchWorkOrders = async () => {
    try {
      // console.log('Fetching work orders from backend...');
      // JWT 토큰 가져오기 및 Authorization 헤더 추가 로직 제거 (axiosInstance가 처리)
      const response = await axiosInstance.get('/work-orders'); // /api 제거
      const fetchedWorkOrders = response.data;
      // console.log('Fetched work orders:', fetchedWorkOrders);

      const newColumns = {
        'todo': { name: 'To Do', items: [] },
        'in-progress': { name: 'In Progress', items: [] },
        'review': { name: 'Review', items: [] },
        'done': { name: 'Done', items: [] },
      };

      const today = new Date();
      const year = today.getFullYear();
      const month = (today.getMonth() + 1).toString().padStart(2, '0');
      const day = today.getDate().toString().padStart(2, '0');
      const todayFormatted = `${year}${month}${day}`;

      fetchedWorkOrders.forEach(order => {
        order.id = String(order.id);
        // console.log(`Order ${order.id} has status: ${order.status}`);
        let parsedDetails = {}; // 파싱된 details를 저장할 변수

        if (order.details && typeof order.details === 'string') {
          try {
            parsedDetails = JSON.parse(order.details);
            // console.log('WorkOrderPage - useEffect - Parsed order.details:', parsedDetails); // 디버깅용 로그 추가
            if (order.type === '시공통보') {
              // console.log('Parsed details for 시공통보:', parsedDetails);
            }
          } catch (e) {
            console.error("Failed to parse details JSON for order:", order.id, e);
            parsedDetails = {};
          }
        }

        // newColumns에 추가할 때 parsedDetails를 포함한 새로운 객체를 생성
        const orderToAdd = { ...order, details: parsedDetails };

        if (order.status === 'done') {
          const orderCompletedDate = order.completedAt ? order.completedAt.substring(0, 10).replace(/-/g, '') : null;
          if (orderCompletedDate === todayFormatted) {
            newColumns[order.status].items.push(orderToAdd); // 수정된 객체 사용
          }
        } else if (newColumns[order.status]) {
          newColumns[order.status].items.push(orderToAdd); // 수정된 객체 사용
        } else {
          console.warn(`Unknown status for order ${order.id}: ${order.status}`);
        }
      });
      // console.log('Final columns state:', newColumns);
      setColumns(newColumns);
    } catch (error) {
      console.error('Error fetching work orders:', error);
    }
  };

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  }, [setTasks]);

  useEffect(() => {
    fetchWorkOrders();
    fetchTasks(); // Call fetchTasks here
  }, [setColumns, fetchTasks]); // Add fetchTasks to dependency array

  const findContainer = (id) => {
    if (id in columns) {
      return id;
    }
    return Object.keys(columns).find((key) =>
      columns[key].items.some((item) => String(item.id) === String(id))
    );
  };

  const handleDragStart = (event) => {
    const activeId = String(event.active.id);
    setActiveId(activeId);

    // 드래그 시작 시점의 컨테이너 저장
    const startContainer = findContainer(activeId);
    setDragStartContainer(startContainer);
    // console.log(`🎯 Drag started: item ${activeId} from container ${startContainer}`);
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    if (activeId === overId) return;

    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId) || overId;

    // console.log("handleDragEnd - activeContainer:", activeContainer, "overContainer:", overContainer); 

    if (!activeContainer) return;

    // 다른 컬럼으로 이동하는 경우 - UI만 업데이트 (백엔드 호출은 dragEnd에서)
    if (activeContainer !== overContainer && overContainer in columns) {
      // setColumns((prev) => {
      //   const activeItems = [...prev[activeContainer].items];
      //   const overItems = [...prev[overContainer].items];

      //   const activeIndex = activeItems.findIndex(item => String(item.id) === activeId);
      //   const activeItem = activeItems[activeIndex];

      //   if (!activeItem) return prev;

      //   // 아이템을 소스 컬럼에서 제거
      //   activeItems.splice(activeIndex, 1);

      //   // 타겟 컬럼에 추가 (맨 끝에) - 아직 status는 변경하지 않음
      //   overItems.push({
      //     ...activeItem,
      //     // status는 dragEnd에서 변경할 예정이므로 여기서는 변경하지 않음
      //   });

      //   return {
      //     ...prev,
      //     [activeContainer]: {
      //       ...prev[activeContainer],
      //       items: activeItems,
      //     },
      //     [overContainer]: {
      //       ...prev[overContainer],
      //       items: overItems,
      //     },
      //   };
      // });
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    const activeContainer = findContainer(activeId);
    const overContainer = findContainer(overId) || overId;

    // 다른 컬럼으로 이동하는 경우에만 권한 체크
    if (activeContainer && overContainer && activeContainer !== overContainer && overContainer in columns) {
      if (user?.role !== 'ADMIN') {
        Swal.fire({
          icon: 'error',
          title: '권한 없음',
          text: '드래그 앤 드롭으로 상태를 변경할 권한이 없습니다.',
        });
        return; // 권한이 없으면 여기서 함수 종료
      }
    }

    if (activeId === overId) return;
    if (!activeContainer) return;

    const previousColumns = JSON.parse(JSON.stringify(columns));

    // 같은 컬럼 내에서 순서 변경
    if (activeContainer === overContainer && overContainer in columns) {
      const items = [...columns[activeContainer].items];
      const oldIndex = items.findIndex(i => String(i.id) === activeId);
      const newIndex = items.findIndex(i => String(i.id) === overId);

      if (oldIndex === -1 || newIndex === -1) return;

      const newItems = arrayMove(items, oldIndex, newIndex);
      setColumns(prev => ({
        ...prev,
        [activeContainer]: {
          ...prev[activeContainer],
          items: newItems,
        },
      }));
    } 
    // 다른 컬럼으로 이동
    else if (activeContainer !== overContainer && overContainer in columns) {
      // console.log("Different column drop detected. activeContainer:", activeContainer, "overContainer:", overContainer);
      const activeItem = columns[activeContainer].items.find(i => String(i.id) === activeId);
      if (!activeItem) return;

      let updatedItem = { ...activeItem, status: overContainer };
      if (overContainer === 'done') {
        const today = new Date();
        const year = today.getFullYear();
        const month = (today.getMonth() + 1).toString().padStart(2, '0');
        const day = today.getDate().toString().padStart(2, '0');
        updatedItem.completedAt = `${year}${month}${day}`;

        // WorkOrder가 done으로 이동할 때 ScheduleEntry 상태 업데이트
        if (activeItem.type === '배치' && activeItem.details && activeItem.details.assignments) {
          for (const assignment of activeItem.details.assignments) {
            const taskId = assignment.taskId;
            const scheduledDate = assignment.assignmentDate;

            if (taskId && scheduledDate) {
              try {
                await axiosInstance.put(`/schedule-entries/${taskId}/update-deployment-status`, null, {
                  params: {
                    scheduledDate: scheduledDate,
                    newStatus: '배치완료'
                  }
                });
                // console.log(`ScheduleEntry deploymentStatus updated to '배치완료' for taskId: ${taskId}, scheduledDate: ${scheduledDate}`);

                // New logic: Update taskStatus if projectType is '준공'
                if (assignment.projectType === '준공') {
                  const taskToUpdate = tasks.find(t => t.id === taskId);
                  if (taskToUpdate && taskToUpdate.taskStatus === '공사중') {
                    try {
                      const updatedTaskData = { ...taskToUpdate, taskStatus: '준공' };
                      await axiosInstance.put(`/tasks/${taskToUpdate.id}`, updatedTaskData);
                      console.log(`Task ${taskToUpdate.id} status updated to '준공' from WorkOrderPage.`);
                    } catch (taskError) {
                      console.error('Error updating task status to "준공" from WorkOrderPage:', taskError);
                      // Optionally, inform the user that the secondary update failed
                    }
                  }
                }

              } catch (scheduleEntryError) {
                console.error('Error updating related ScheduleEntry deploymentStatus:', scheduleEntryError);
              }
            }
          }
        }
      } else {
        updatedItem.completedAt = null;
      }

      const requestBody = {
        title: updatedItem.title,
        type: updatedItem.type,
        details: typeof updatedItem.details === 'object' ? JSON.stringify(updatedItem.details) : updatedItem.details,
        status: updatedItem.status,
      };

      try {
        await axiosInstance.put(`/work-orders/${updatedItem.id}`, requestBody); // /api 제거

        // WorkOrder가 done에서 다른 컬럼으로 이동할 때 ScheduleEntry 상태 업데이트
        if (activeItem.type === '배치' && activeItem.details && activeItem.details.assignments && activeContainer === 'done' && overContainer !== 'done') {
          for (const assignment of activeItem.details.assignments) {
            const taskId = assignment.taskId;
            const scheduledDate = assignment.assignmentDate;

            if (taskId && scheduledDate) {
              try {
                await axiosInstance.put(`/schedule-entries/${taskId}/update-deployment-status`, null, {
                  params: {
                    scheduledDate: scheduledDate,
                    newStatus: '배치지시'
                  }
                });
                // console.log(`ScheduleEntry deploymentStatus updated to '배치지시' for taskId: ${taskId}, scheduledDate: ${scheduledDate}`);

                // New logic: Update taskStatus to '공사중' if it was '준공'
                const taskToUpdate = tasks.find(t => t.id === taskId);
                if (taskToUpdate && taskToUpdate.taskStatus === '준공') {
                  try {
                    const updatedTaskData = { ...taskToUpdate, taskStatus: '공사중' };
                    await axiosInstance.put(`/tasks/${taskToUpdate.id}`, updatedTaskData);
                    console.log(`Task ${taskToUpdate.id} status updated to '공사중' from WorkOrderPage (moved out of done).`);
                  } catch (taskError) {
                    console.error('Error updating task status to "공사중" from WorkOrderPage (moved out of done):', taskError);
                  }
                }
              } catch (scheduleEntryError) {
                console.error('Error updating related ScheduleEntry deploymentStatus:', scheduleEntryError);
              }
            }
          }
        }

        setColumns(prevColumns => {
          const newCols = { ...prevColumns };
          // Remove from activeContainer
          newCols[activeContainer] = {
            ...newCols[activeContainer],
            items: newCols[activeContainer].items.filter(item => String(item.id) !== String(activeId))
          };
          // Add to overContainer
          newCols[overContainer] = {
            ...newCols[overContainer],
            items: [...newCols[overContainer].items, updatedItem]
          };
          return newCols;
        });
        fetchTasks(); // Call fetchTasks here
      } catch (error) {
        console.error('Error updating work order status. Full error object:', error);
        if (error.response) {
          console.error('Backend responded with:', error.response.data);
        }
        setColumns(previousColumns); // 에러 시 롤백
      }
    }
  };

  const handleAddWorkOrder = (createdOrder) => {
    setColumns(prevColumns => ({
      ...prevColumns,
      todo: {
        ...prevColumns.todo,
        items: [...prevColumns.todo.items, createdOrder],
      },
    }));
    window.location.reload(); // 새로고침 추가
  };

  const handleDeleteWorkOrder = async (workOrderId, columnId) => {
    const workOrderToDelete = columns[columnId]?.items.find(item => String(item.id) === String(workOrderId));

    if (!workOrderToDelete) {
      console.error('Work order to delete not found');
      return;
    }

    try {
      // 1. 연결된 ScheduleEntry 상태를 '배치계획'으로 변경
      if (workOrderToDelete.type === '배치' && workOrderToDelete.details && workOrderToDelete.details.assignments) {
        for (const assignment of workOrderToDelete.details.assignments) {
          try {
            await axiosInstance.put(`/schedule-entries/${assignment.taskId}/update-deployment-status`, null, {
              params: {
                scheduledDate: assignment.assignmentDate,
                newStatus: '배치계획'
              }
            });
            // New logic: Update taskStatus to '공사중' if it was '준공'
            const taskToUpdate = tasks.find(t => t.id === assignment.taskId);
            if (taskToUpdate && taskToUpdate.taskStatus === '준공') {
              try {
                const updatedTaskData = { ...taskToUpdate, taskStatus: '공사중' };
                await axiosInstance.put(`/tasks/${taskToUpdate.id}`, updatedTaskData);
                console.log(`Task ${taskToUpdate.id} status updated to '공사중' from WorkOrderPage (deleted).`);
              } catch (taskError) {
                console.error('Error updating task status to "공사중" from WorkOrderPage (deleted):', taskError);
              }
            }
          } catch (error) {
            console.error(`Failed to update schedule entry for task ${assignment.taskId}`, error);
            // 에러가 발생해도 삭제는 계속 진행하도록 할 수 있습니다.
          }
        }
      }

      // 2. 작업지시 삭제 API 호출
      await axiosInstance.delete(`/work-orders/${workOrderId}`);

      // 3. UI에서 카드 제거
      setColumns(prevColumns => ({
        ...prevColumns,
        [columnId]: {
          ...prevColumns[columnId],
          items: prevColumns[columnId].items.filter(item => String(item.id) !== String(workOrderId)),
        },
      }));
    } catch (error) {
      console.error('Error deleting work order:', error);
    }
  };

  const handleOpenDetailModal = useCallback((item) => {
    setSelectedWorkOrder(item);
    setIsDetailModalOpen(true);
  }, []);

  // 모든 아이템의 ID만 수집 (컬럼 ID는 제외)
  const allItemIds = Object.values(columns).flatMap(column => 
    column.items.map(item => item.id)
  );

  return (
    <div className="work-order-page-container">
      {/* <h2>작업 지시</h2> */}
      <br/><br/>
      <div className="button-group">
        {user && ['ADMIN', 'MIDADMIN'].includes(user.role) && (
        <button onClick={() => setIsCreateModalOpen(true)} className="register-button">
          작업지시 티켓 생성
        </button>
        )}
        {user && ['ADMIN', 'MIDADMIN', 'USER'].includes(user.role) && (
        <button onClick={handleOpenHistoryModal} className="history-button">
          작업 처리 내역
        </button>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <SortableContext 
          items={allItemIds}
          strategy={verticalListSortingStrategy}
        >
          <div className="kanban-board">
            {Object.entries(columns).map(([columnId, column]) => (
              <DroppableColumn
                key={columnId}
                columnId={columnId}
                column={column}
                handleOpenDetailModal={handleOpenDetailModal}
              />
            ))}
          </div>
        </SortableContext>
        
        <DragOverlay>
          {activeId && columns ? (
            (() => {
              const activeItem = Object.values(columns)
                .flatMap(column => column.items)
                .find(item => String(item.id) === String(activeId));
              
              return activeItem ? (
                <div className="kanban-card dragging">
                  <MemoizedCardContent item={activeItem} handleOpenDetailModal={() => {}} />
                </div>
              ) : null;
            })()
          ) : null}
        </DragOverlay>
      </DndContext>

      <CreateWorkOrderModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreateWorkOrder={handleAddWorkOrder}
        initialSelectedEntry={initialScheduleEntry}
      />

      <WorkOrderDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        workOrder={selectedWorkOrder}
        onDelete={handleDeleteWorkOrder}
      />

      <WorkOrderHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        doneWorkOrders={historyWorkOrders}
      />
    </div>
  );
}

export default WorkOrderPage;